/** O que uma ação feita item a item deu: o que deu certo e o que falhou, com o erro. */
export interface ResultadoEmLote<T> {
  readonly feitos: readonly T[];
  readonly falhas: readonly { readonly item: T; readonly erro: unknown }[];
}

/**
 * A mesma ação em vários itens, poucos de cada vez, pelo caminho da ação de um
 * item só — as regras de cada um continuam valendo. Uma falha não para as
 * outras: o resultado diz o que deu certo e o que não, na ordem da lista.
 */
export async function executarEmLote<T>(
  itens: readonly T[],
  acao: (item: T) => Promise<unknown>,
  simultaneos = 4,
): Promise<ResultadoEmLote<T>> {
  const feitos: number[] = [];
  const falhas: { indice: number; erro: unknown }[] = [];
  let proximo = 0;

  const trabalhar = async (): Promise<void> => {
    while (proximo < itens.length) {
      const indice = proximo;
      proximo += 1;
      try {
        await acao(itens[indice] as T);
        feitos.push(indice);
      } catch (erro) {
        falhas.push({ indice, erro });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(simultaneos, itens.length) }, trabalhar));

  // Na ordem da lista, não na de chegada das respostas.
  return {
    feitos: feitos.sort((a, b) => a - b).map((i) => itens[i] as T),
    falhas: falhas
      .sort((a, b) => a.indice - b.indice)
      .map((f) => ({ item: itens[f.indice] as T, erro: f.erro })),
  };
}

/** Os primeiros nomes de um lote, para o diálogo: "Ana, Bruno, Carla e mais 4". */
export function resumirNomes(nomes: readonly string[], quantos = 3): string {
  if (nomes.length > quantos) {
    return `${nomes.slice(0, quantos).join(', ')} e mais ${nomes.length - quantos}`;
  }
  if (nomes.length <= 1) return nomes[0] ?? '';
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1] ?? ''}`;
}
